import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { useExpenditureLabels, useDecimalFormat } from '../../../contexts/ExpenditureLabelsContext';
import TypeAheadLookup from '../../../components/TypeAheadLookup';
import { LoadingSpinner, EmptyState } from '../../../components/layout/SharedComponents';
import {
  ChevronDown, ChevronLeft, ChevronRight, X, Plus, Trash2, Copy, ChevronsDown,
  Layers, FileText, CheckSquare, Split, Paperclip,
  ScrollText, StickyNote, Package, Activity, Save,
  TrendingUp, BarChart2, Upload, Search, Wand2, Download,
  RefreshCw, Edit, FileSpreadsheet, ListOrdered, AlignJustify, Users
} from 'lucide-react';
import LineItemDetailEditor from './LineItemDetailEditor';
import DragSortList from '../../../components/DragSortList';
import AddCatalogItemModal from '../modals/AddCatalogItemModal';
import AddPunchoutModal from '../modals/AddPunchoutModal';
import { useConfirm } from '../../../components/ConfirmDialog';
import ItemSourcingModal from '../modals/ItemSourcingModal';
import UpdateAllModal from '../modals/UpdateAllModal';
import { useAuth } from '../../../contexts/AuthContext';

export default function MainDetailsPanel({ req, canEdit, requestNumber, addingItem, setAddingItem, editingItemId, setEditingItemId, addLineItem, updateLineItem, deleteLineItem, submitReq, onReload, netTotal, segments, mandatoryLineFields }) {
  const { systemSettings } = useAuth();
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const { fmtVal, fmtTotal } = useDecimalFormat();
  const [actionsOpen, setActionsOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [punchoutOpen, setPunchoutOpen] = useState(false);
  const [sourcingOpen, setSourcingOpen] = useState(false);
  const [updateAllOpen, setUpdateAllOpen] = useState(false);
  const [reorderOpen, setReorderOpen] = useState(false);
  const actionsRef = useRef(null);
  const xlsxRef = useRef(null);

  useEffect(() => {
    const h = e => { if (actionsRef.current && !actionsRef.current.contains(e.target)) setActionsOpen(false); };
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
  }, []);

  const reindex = async (newOrder) => {
    const items = newOrder || (req.line_items||[]);
    const ids = items.map(i => i.id);
    try {
      await api.post(`/api/requisitions/${requestNumber}/reindex`, { item_ids: ids });
      toast.success('Line items re-ordered');
      onReload();
    } catch (err) { if (err?.code !== 'ERR_CANCELED' && err?.response?.status !== 401 && err?.response?.status !== 403) toast.error('Re-index failed'); }
  };

  const handleAddCatalogItem = async (item) => {
    // Carry over expense codes from the last line unless item has its own
    const lines = req.line_items || [];
    const lastLine = lines.length > 0 ? lines[lines.length - 1] : null;
    await addLineItem({
      item_code: item.item_code, item_description: item.outline, quantity: 1,
      unit: 'EACH', pack_size: 1, discount: 0, unit_price: 0, supplier_code: '',
      account_code_4: item.account_code_4 || lastLine?.account_code_4 || '',
      account_code_5: item.account_code_5 || lastLine?.account_code_5 || '',
      account_code_6: item.account_code_6 || lastLine?.account_code_6 || '',
      expense_type: item.expense_type || lastLine?.expense_type || 'None',
    });
  };

  const handleImportExcel = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws);
        let added = 0;
        for (const row of data) {
          const item = {
            item_code: String(row['Item Code'] || row['item_code'] || ''),
            item_description: String(row['Description'] || row['item_description'] || row['Item Description'] || ''),
            quantity: parseFloat(row['Quantity'] || row['quantity'] || 1),
            unit: String(row['Unit'] || row['unit'] || 'EACH'),
            pack_size: parseInt(row['Pack Size'] || 1),
            unit_price: parseFloat(row['Unit Price'] || row['unit_price'] || 0),
            discount: parseFloat(row['Discount'] || 0),
            supplier_code: String(row['Supplier Code'] || ''),
          };
          if (item.item_description) { await addLineItem(item); added++; }
        }
        toast.success(`${added} items imported from Excel`);
        onReload();
      } catch (err) { toast.error('Failed to parse Excel file. Check format.'); }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const downloadExcelTemplate = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet([
      { 'Item Code': '', 'Item Description': 'Sample item description', 'Quantity': 1, 'Unit': 'EACH', 'Pack Size': 1, 'Unit Price': 0, 'Discount': 0, 'Supplier Code': '', 'Item Due Date': '' }
    ]);
    XLSX.utils.book_append_sheet(wb, ws, 'Line Items');
    XLSX.writeFile(wb, `Requisition_${requestNumber}_Template.xlsx`);
    toast.success('Template downloaded');
  };

  const ACTIONS = [
    { icon: Plus,          label: 'Add New',                  action: () => { setAddingItem(true); setActionsOpen(false); } },
    { icon: Search,        label: 'Add Catalog Item',         action: () => { setCatalogOpen(true); setActionsOpen(false); } },
    { icon: Package,       label: 'Add Punchout Items',       action: () => { setPunchoutOpen(true); setActionsOpen(false); } },
    { icon: Wand2,         label: 'Item Sourcing',            action: () => { setSourcingOpen(true); setActionsOpen(false); }, highlight: true },
    { icon: ListOrdered,   label: 'Re-Index',                 action: () => { setReorderOpen(true); setActionsOpen(false); } },
    { icon: RefreshCw,     label: 'Update All',               action: () => { setUpdateAllOpen(true); setActionsOpen(false); } },
    { icon: FileSpreadsheet, label: 'Import Items from MS-Excel', action: () => { xlsxRef.current?.click(); setActionsOpen(false); } },
    { icon: Download,      label: 'Download Excel Template',  action: () => { downloadExcelTemplate(); setActionsOpen(false); } },
    { icon: Trash2,        label: 'Delete',                   action: () => { deleteSelected(); setActionsOpen(false); }, danger: true },
  ];

  return (
    <div>
      {/* Hidden Excel input */}
      <input type="file" ref={xlsxRef} accept=".xlsx,.xls,.csv" className="hidden" onChange={handleImportExcel} aria-label="Import from Excel" />

      <Tabs defaultValue="line-items">
        {/* Header row: Main Details actions + Line Items / Group Items toggle */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="relative" ref={actionsRef}>
              <button
                onClick={() => setActionsOpen(v => !v)}
                className="flex items-center gap-1.5 font-bold text-primary hover:opacity-80"
                aria-expanded={actionsOpen} aria-label="Main Details actions"
              >
                <ChevronsDown className="h-5 w-5 text-orange-500" strokeWidth={3} /> Main Details
              </button>
              {actionsOpen && (
                <div className="absolute top-full left-0 mt-1 z-50 bg-card border rounded-xl shadow-xl p-4 w-56">
                  <p className="font-bold text-sm mb-2">Actions</p>
                  {ACTIONS.map((a, i) => (
                    <button key={i} onClick={a.action}
                      className={`flex items-center gap-2.5 w-full text-left text-sm py-2 border-b last:border-0 hover:text-primary transition-colors ${a.danger ? 'text-destructive' : a.highlight ? 'text-primary font-medium' : ''}`}>
                      <a.icon className="h-4 w-4 flex-shrink-0" />
                      {a.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <TabsList>
              <TabsTrigger value="line-items">
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 rounded-full bg-primary text-white text-[10px] flex items-center justify-center">{(req.line_items||[]).length}</span>
                  Line Items
                </span>
              </TabsTrigger>
              <TabsTrigger value="group-items">Group Items</TabsTrigger>
            </TabsList>
          </div>
        </div>

        <TabsContent value="line-items">
          {/* Add New Item editor */}
          {addingItem && canEdit && (
            <div className="mb-3">
              <LineItemDetailEditor
                item={(() => {
                  // Carry over expense codes 4,5,6 from the last line item
                  const lines = req.line_items || [];
                  const lastLine = lines.length > 0 ? lines[lines.length - 1] : null;
                  return {
                    item_code: '', item_description: '', item_due_date: '',
                    quantity: 1, unit: 'EACH', pack_size: 1, discount: 0,
                    unit_price: 0, supplier_code: '', suppliers_part_number: '',
                    sales_tax: 0, expense_type: lastLine?.expense_type || 'None', co2e: 0,
                    account_code_4: lastLine?.account_code_4 || '',
                    account_code_5: lastLine?.account_code_5 || '',
                    account_code_6: lastLine?.account_code_6 || '',
                  };
                })()}
                requestNumber={requestNumber}
                onSave={async (d) => { await addLineItem(d); }}
                onDelete={() => setAddingItem(false)}
                onCancel={() => setAddingItem(false)}
                mandatoryLineFields={mandatoryLineFields || []}
                isNew
              />
            </div>
          )}
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-muted/50">
                {[
                  { label: 'Item', align: 'text-left' },
                  { label: 'Item Description', align: 'text-left' },
                  { label: 'Supplier', align: 'text-left' },
                  { label: 'Item Due Date', align: 'text-left' },
                  { label: 'Quantity', align: 'text-right' },
                  { label: 'Unit', align: 'text-left' },
                  { label: 'Pack Size', align: 'text-right' },
                  { label: 'Discount', align: 'text-right' },
                  { label: 'Unit Price', align: 'text-right' },
                  { label: 'Net Price', align: 'text-right' },
                  ...(canEdit ? [{ label: '', align: '' }] : []),
                ].map(h2 => (
                  <th key={h2.label || 'actions'} className={`${h2.align} px-2 py-2 font-semibold text-muted-foreground border-b`}>{h2.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(req.line_items || []).map((item, idx) => (
                <React.Fragment key={item.id}>
                  <tr className={`border-b hover:bg-accent/30 cursor-pointer ${editingItemId === item.id ? 'bg-primary/5' : ''}`}
                    onClick={() => setEditingItemId(editingItemId === item.id ? null : item.id)}>
                    <td className="px-2 py-1.5">{item.item_num}</td>
                    <td className="px-2 py-1.5 font-medium max-w-[250px] truncate">{item.item_description}</td>
                    <td className="px-2 py-1.5 text-muted-foreground max-w-[150px] truncate">{item.supplier_name || item.supplier_code || '—'}</td>
                    <td className="px-2 py-1.5">{item.item_due_date ? new Date(item.item_due_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz }) : ''}</td>
                    <td className="px-2 py-1.5 text-right">{fmtVal(item.quantity)}</td>
                    <td className="px-2 py-1.5">{item.unit}</td>
                    <td className="px-2 py-1.5 text-right">{item.pack_size}</td>
                    <td className="px-2 py-1.5 text-right">{fmtVal(item.discount)}%</td>
                    <td className="px-2 py-1.5 text-right">{fmtVal(item.unit_price)}</td>
                    <td className="px-2 py-1.5 text-right font-semibold text-primary">{fmtTotal(item.net_price)}</td>
                    {canEdit && (
                      <td className="px-2 py-1.5" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <button onClick={() => {
                            const maxNum = Math.max(0, ...(req.line_items || []).map(li => li.item_num || 0));
                            const copy = { ...item, id: undefined, item_num: maxNum + 1 };
                            delete copy.id;
                            addLineItem(copy);
                          }} className="text-blue-600 hover:opacity-70" title="Copy line item"><Copy className="h-3.5 w-3.5" /></button>
                          <button onClick={() => deleteLineItem(item.id)} className="text-destructive hover:opacity-70" title="Delete line item"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                  {/* Expanded detail — editable or view-only */}
                  {editingItemId === item.id && (
                    <tr><td colSpan={canEdit ? 10 : 9} className="p-0">
                      <LineItemDetailEditor item={item} requestNumber={requestNumber}
                        onSave={canEdit ? async (d) => { await updateLineItem(item.id, d); } : null}
                        onDelete={canEdit ? () => { deleteLineItem(item.id); setEditingItemId(null); } : null}
                        onCancel={() => setEditingItemId(null)}
                        readOnly={!canEdit}
                        mandatoryLineFields={mandatoryLineFields || []} />
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t font-semibold bg-muted/30">
                <td colSpan={9} className="px-2 py-2 text-sm">Total Items: {(req.line_items||[]).length}</td>
                <td className="px-2 py-2 text-right text-sm">Total USD:</td>
                <td className="px-2 py-2 text-right font-bold text-primary">{fmtTotal(netTotal)}</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>

          {/* Submit for Approval is on the Approvers panel */}
        </TabsContent>

        <TabsContent value="group-items">
          <GroupItemsTab requestNumber={requestNumber} canEdit={canEdit} onReload={onReload} />
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <AddCatalogItemModal open={catalogOpen} onClose={() => setCatalogOpen(false)} onAdd={handleAddCatalogItem} />
      <AddPunchoutModal open={punchoutOpen} onClose={() => setPunchoutOpen(false)} requestNumber={requestNumber} onAddItems={async (items) => {
        for (const it of items) {
          await addLineItem({
            item_code: it.item_code || '',
            item_description: it.item_description || it.outline || it.description || '',
            quantity: it.quantity || 1,
            unit: it.unit || 'EACH',
            pack_size: it.pack_size || 1,
            discount: it.discount || 0,
            unit_price: it.unit_price || 0,
            supplier_code: it.supplier_code || '',
            suppliers_part_number: it.suppliers_part_number || '',
          });
        }
        onReload();
      }} />
      <ItemSourcingModal open={sourcingOpen} onClose={() => setSourcingOpen(false)} onAdd={item => addLineItem(item)} />
      <UpdateAllModal open={updateAllOpen} onClose={() => setUpdateAllOpen(false)} req={req} segments={segments} onSaved={onReload} />

      {/* Reorder Dialog with drag-and-drop */}
      {reorderOpen && (
        <ReorderDialog items={req.line_items || []} onSave={reindex} onClose={() => setReorderOpen(false)} />
      )}
    </div>
  );
}


function GroupItemsTab({ requestNumber, canEdit, onReload }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reqLineItems, setReqLineItems] = useState([]);
  const confirm = useConfirm();

  useEffect(() => {
    api.get('/api/data/group-items', { params: { page_size: 100 } })
      .then(r => setGroups(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    api.get(`/api/requisitions/${requestNumber}`)
      .then(r => setReqLineItems(r.data?.line_items || []))
      .catch(() => {});
  }, [requestNumber]);

  const addGroupToReq = async (group) => {
    try {
      for (const gi of (group.items || [])) {
        await api.post(`/api/requisitions/${requestNumber}/line-items`, {
          item_code: gi.item_code,
          item_description: `[Group: ${group.name}] ${gi.outline || gi.item_code}`,
          quantity: gi.quantity || 1,
          unit_price: gi.unit_price || 0,
          unit: gi.unit || 'EACH',
          group_id: group.group_id,
          group_name: group.name,
        });
      }
      toast.success(`Added ${group.items?.length || 0} items from "${group.name}"`);
      api.get(`/api/requisitions/${requestNumber}`).then(r => setReqLineItems(r.data?.line_items || [])).catch(() => {});
    } catch { toast.error('Failed to add group items'); }
  };

  const removeGroupFromReq = async (groupName) => {
    if (!await confirm(`Remove all line items from group "${groupName}"?`, { title: 'Remove Group Items', destructive: true })) return;
    try {
      const toRemove = reqLineItems.filter(li => li.item_description?.startsWith(`[Group: ${groupName}]`) || li.group_name === groupName);
      for (const li of toRemove) {
        await api.delete(`/api/requisitions/${requestNumber}/line-items/${li.id}`);
      }
      toast.success(`Removed ${toRemove.length} items from "${groupName}"`);
      api.get(`/api/requisitions/${requestNumber}`).then(r => setReqLineItems(r.data?.line_items || [])).catch(() => {});
    } catch { toast.error('Failed to remove group items'); }
  };

  // Find which groups are already on this requisition
  const addedGroups = {};
  for (const li of reqLineItems) {
    const match = li.item_description?.match(/^\[Group: (.+?)\]/);
    const gn = match?.[1] || li.group_name;
    if (gn) {
      if (!addedGroups[gn]) addedGroups[gn] = [];
      addedGroups[gn].push(li);
    }
  }
  const addedGroupNames = Object.keys(addedGroups);

  if (loading) return <p className="text-xs text-muted-foreground text-center py-4">Loading groups...</p>;

  return (
    <div className="space-y-4">
      {/* Groups already on this requisition */}
      {addedGroupNames.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Groups on this Requisition</p>
          <div className="space-y-2">
            {addedGroupNames.map(gn => (
              <div key={gn} className="flex items-center gap-2 p-3 border rounded-lg border-primary/20 bg-primary/5">
                {canEdit && (
                  <button onClick={() => removeGroupFromReq(gn)} className="text-destructive hover:opacity-70 flex-shrink-0">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
                <div className="flex-1">
                  <p className="text-sm font-semibold text-primary">{gn}</p>
                  <p className="text-xs text-muted-foreground">{addedGroups[gn].length} line item(s)</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Available groups — click to add */}
      {canEdit && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Available Groups — click to add</p>
          {groups.filter(g => !addedGroupNames.includes(g.name)).length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-3">
              {groups.length === 0 ? 'No group items configured. Create groups in Data Files > Group Items.' : 'All available groups have been added.'}
            </p>
          ) : (
            <div className="space-y-1.5">
              {groups.filter(g => !addedGroupNames.includes(g.name)).map(g => (
                <div key={g.group_id} onClick={() => addGroupToReq(g)} className="flex items-center gap-3 p-2.5 border rounded-lg hover:bg-primary/10 hover:border-primary/30 cursor-pointer transition-colors">
                  <Plus className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{g.name}</p>
                    <p className="text-[10px] text-muted-foreground">{g.description} &middot; {(g.items||[]).length} items</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {addedGroupNames.length === 0 && !canEdit && (
        <p className="text-sm text-muted-foreground text-center py-4">No group items on this requisition.</p>
      )}
    </div>
  );
}


function ReorderDialog({ items: initialItems, onSave, onClose }) {
  const [items, setItems] = useState([...initialItems]);
  const [saving, setSaving] = useState(false);

  const doSave = async () => {
    setSaving(true);
    await onSave(items);
    setSaving(false);
    onClose();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Re-Index Line Items</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground mb-3">Drag items to reorder. Item numbers will be resequenced when you save.</p>
        <div className="border rounded-lg overflow-hidden p-2">
          <DragSortList
            items={items}
            keyField="id"
            onReorder={setItems}
            renderItem={(item, idx) => (
              <div className="flex items-center gap-3 py-2 border-b last:border-0">
                <span className="font-mono text-primary font-bold w-6 text-center">{idx + 1}</span>
                <span className="text-sm flex-1 truncate">{item.item_description}</span>
                <span className="text-xs text-muted-foreground font-mono">{item.quantity} {item.unit}</span>
              </div>
            )}
          />
        </div>
        <DialogFooter>
          <Button onClick={doSave} disabled={saving}><Save className="h-4 w-4 mr-1" /> {saving ? 'Saving...' : 'Save Order'}</Button>
          <Button variant="outline" onClick={onClose}><X className="h-4 w-4 mr-1" /> Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
